import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import media from "styled-media-query";

import ActivitiesCell, { ActivityCellProps } from "../cell/ActivitiesCell";
import { Text, ColorV2 } from "@adoptaunabuelo/react-components";
import { ChevronRight, ChevronLeft } from "lucide-react";
import VideoModal from "../modal/VideoModal";

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
	width: 100%;
	min-width: 0;
`;
const Track = styled.div`
	display: flex;
	gap: 16px;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	min-width: 0;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	${media.lessThan("medium")`
        box-sizing: border-box;
        width: 100vw;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        padding-left: 24px;
        padding-right: 24px;
        scroll-padding-left: 24px;
    `};
`;
const CellWrap = styled.div`
	scroll-snap-align: start;
	flex: 0 0 100%;
	min-width: 0;
	${media.lessThan("medium")`
        flex: 0 0 calc(100vw - 50px);
    `};
`;
const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const VerVideoButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 10px 20px;
	border-radius: 1000px;
	border: 1px solid ${ColorV2.border.neutralHard};
	background-color: white;
	cursor: pointer;
	&:hover {
		background-color: ${ColorV2.surface.background};
	}
`;
const VerVideoText = styled(Text)`
	font-size: 14px !important;
	line-height: 20px !important;
	font-weight: 500 !important;
	color: ${ColorV2.text.neutralHard};
	margin: 0;
`;
const ChevronGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const ChevronButton = styled.button<{ $disabled: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 40px;
	width: 40px;
	padding: 0;
	border-radius: 1000px;
	border: 1px solid ${ColorV2.border.neutralHard};
	background-color: white;
	cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
	opacity: ${(p) => (p.$disabled ? 0.3 : 1)};
	&:hover {
		background-color: ${(p) =>
		p.$disabled ? "white" : ColorV2.surface.background};
	}
`;

const ActivitiesList = (props: Props) => {
	const [index, setIndex] = useState(0);
	const [showVideoModal, setShowVideoModal] = useState(false);
	const trackRef = useRef<HTMLDivElement>(null);

	const scrollToIndex = (newIndex: number) => {
		const track = trackRef.current;
		if (!track) return;
		const firstCell = track.children[0] as HTMLElement | undefined;
		if (!firstCell) return;
		const cellWidth = firstCell.offsetWidth;
		const gap = 16;
		track.scrollTo({
			left: newIndex * (cellWidth + gap),
			behavior: "smooth",
		});
	};

	useEffect(() => {
		const track = trackRef.current;
		if (!track) return;
		let timeout: ReturnType<typeof setTimeout>;
		const onScroll = () => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				const firstCell = track.children[0] as HTMLElement | undefined;
				if (!firstCell) return;
				const cellWidth = firstCell.offsetWidth;
				const gap = 16;
				const newIndex = Math.round(track.scrollLeft / (cellWidth + gap));
				setIndex(newIndex);
			}, 120);
		};
		track.addEventListener("scroll", onScroll);
		return () => {
			clearTimeout(timeout);
			track.removeEventListener("scroll", onScroll);
		};
	}, []);

	const onPrev = () => {
		if (index === 0) return;
		const next = index - 1;
		setIndex(next);
		scrollToIndex(next);
	};
	const onNext = () => {
		if (index === props.activities.length - 1) return;
		const next = index + 1;
		setIndex(next);
		scrollToIndex(next);
	};

	return (
		<Container>
			<VideoModal
				isVisible={showVideoModal}
				url={props.activities[index].video}
				onClose={() => setShowVideoModal(false)}
			/>
			<Track ref={trackRef}>
				{props.activities.map((item, i) => (
					<CellWrap key={`activity-${i}`}>
						<ActivitiesCell {...item} />
					</CellWrap>
				))}
			</Track>
			<Footer>
				<VerVideoButton onClick={() => setShowVideoModal(true)}>
					<VerVideoText type="p">Ver vídeo</VerVideoText>
				</VerVideoButton>
				<ChevronGroup>
					<ChevronButton $disabled={index === 0} onClick={onPrev}>
						<ChevronLeft
							height={20}
							width={20}
							color={ColorV2.text.neutralHard}
						/>
					</ChevronButton>
					<ChevronButton
						$disabled={index === props.activities.length - 1}
						onClick={onNext}
					>
						<ChevronRight
							height={20}
							width={20}
							color={ColorV2.text.neutralHard}
						/>
					</ChevronButton>
				</ChevronGroup>
			</Footer>
		</Container>
	);
};
export default ActivitiesList;
export interface Props {
	activities: ActivityCellProps[];
}
